import React, { useState } from "react";
import { LIFE_PHASES, LIFE_CYCLE_CTA, LIFE_RECOMMENDATIONS } from "@/lib/content";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import ScheduleCTA from "@/components/landing/ScheduleCTA";

/* Polar to Cartesian helper for the SVG donut */
const polar = (cx, cy, r, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

/* Build an SVG arc path between two angles */
const arcPath = (cx, cy, rOuter, rInner, startDeg, endDeg) => {
  const start = polar(cx, cy, rOuter, endDeg);
  const end = polar(cx, cy, rOuter, startDeg);
  const startIn = polar(cx, cy, rInner, endDeg);
  const endIn = polar(cx, cy, rInner, startDeg);
  const large = endDeg - startDeg <= 180 ? 0 : 1;
  return [
    `M ${start.x} ${start.y}`,
    `A ${rOuter} ${rOuter} 0 ${large} 0 ${end.x} ${end.y}`,
    `L ${endIn.x} ${endIn.y}`,
    `A ${rInner} ${rInner} 0 ${large} 1 ${startIn.x} ${startIn.y}`,
    "Z",
  ].join(" ");
};

const LifeCycle = () => {
  const [active, setActive] = useState(1);
  const [crossAge, setCrossAge] = useState(60);
  const [recAge, setRecAge] = useState(30);
  const phase = LIFE_PHASES[active];

  /* Find the recommendation matching the current recAge */
  const recommendation =
    LIFE_RECOMMENDATIONS.find((r) => recAge >= r.minAge && recAge <= r.maxAge) ||
    LIFE_RECOMMENDATIONS[LIFE_RECOMMENDATIONS.length - 1];

  /* Chart geometry */
  const X0 = 40;
  const X1 = 580;
  const Y_TOP = 60;
  const Y_BOTTOM = 295;
  const Y_CROSS = 180;
  const crossX = X0 + (crossAge / 80) * (X1 - X0);
  const ctrl1X = (X0 + crossX) / 2;
  const ctrl2X = (crossX + X1) / 2;

  /* Human curve: starts high, dips through cross, ends low */
  const humanPath = `M ${X0} ${Y_TOP} Q ${ctrl1X} ${Y_TOP} ${crossX} ${Y_CROSS} Q ${ctrl2X} ${Y_BOTTOM} ${X1} ${Y_BOTTOM}`;
  const humanFill = `${humanPath} L ${X1} 310 L ${X0} 310 Z`;

  /* Financial curve: starts low, rises through cross, ends high */
  const finPath = `M ${X0} ${Y_BOTTOM + 10} Q ${ctrl1X} ${Y_BOTTOM + 10} ${crossX} ${Y_CROSS} Q ${ctrl2X} ${Y_TOP - 5} ${X1} ${Y_TOP - 5}`;
  const finFill = `${finPath} L ${X1} 310 L ${X0} 310 Z`;

  const cx = 220,
    cy = 220,
    rOuter = 200,
    rInner = 120;

  /* The original image rotates the quadrants visually so 0-20 is top-right
   * (angles 0..90), 20-40 bottom-right (90..180), 40-60 bottom-left (180..270),
   * 60-80 top-left (270..360). Mapping LIFE_PHASES order accordingly: */
  const quadrants = [
    { angles: [0, 90], idx: 0 },     // top-right
    { angles: [90, 180], idx: 1 },   // bottom-right
    { angles: [180, 270], idx: 2 },  // bottom-left
    { angles: [270, 360], idx: 3 },  // top-left
  ];

  return (
    <section
      id="ciclo-vital"
      data-testid="lifecycle-section"
      className="relative bg-[#F9F6F0] text-[#0B0410] py-24 lg:py-32 overflow-hidden"
    >
      {/* Subtle decorative bg */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#3D1565]/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-[#E6D5B8]/20 blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* HEADER */}
        <div className="max-w-3xl mb-16 lg:mb-20">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#3D1565]" />
            <span
              className="text-[11px] tracking-luxury uppercase text-[#3D1565] font-semibold"
              data-testid="lifecycle-eyebrow"
            >
              Tu ciclo vital
            </span>
          </div>
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05]"
            data-testid="lifecycle-headline"
          >
            <span className="font-semibold">28,000 días</span> de vida.
            <br />
            <span className="italic text-[#3D1565]">Una sola oportunidad</span>{" "}
            para diseñarlos bien.
          </h2>
          <p className="mt-6 text-base lg:text-lg text-[#0B0410]/70 max-w-2xl font-light">
            La esperanza de vida promedio en México es de aproximadamente 80
            años — unos 28,000 días divididos en cuatro etapas. Cada etapa
            tiene su rol financiero. Haz clic en cada cuadrante para
            descubrirlo.
          </p>
        </div>

        {/* INTERACTIVE DONUT + DETAIL */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Donut */}
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative w-full max-w-md aspect-square">
              <svg
                viewBox="0 0 440 440"
                className="w-full h-full"
                data-testid="lifecycle-donut"
              >
                {quadrants.map((q) => {
                  const p = LIFE_PHASES[q.idx];
                  const isActive = active === q.idx;
                  return (
                    <path
                      key={q.idx}
                      d={arcPath(cx, cy, rOuter, rInner, q.angles[0], q.angles[1])}
                      fill={p.color}
                      data-testid={`lifecycle-quadrant-${q.idx}`}
                      onClick={() => setActive(q.idx)}
                      onMouseEnter={() => setActive(q.idx)}
                      style={{
                        cursor: "pointer",
                        opacity: isActive ? 1 : 0.45,
                        transform: isActive ? "scale(1.04)" : "scale(1)",
                        transformOrigin: "220px 220px",
                        transition: "all 500ms cubic-bezier(0.2, 0.8, 0.2, 1)",
                      }}
                    />
                  );
                })}

                {/* Range labels around the ring */}
                <g style={{ pointerEvents: "none" }}>
                  {quadrants.map((q) => {
                    const mid = (q.angles[0] + q.angles[1]) / 2;
                    const pos = polar(cx, cy, rOuter + 22, mid);
                    const p = LIFE_PHASES[q.idx];
                    return (
                      <text
                        key={q.idx}
                        x={pos.x}
                        y={pos.y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="font-display"
                        fill="#0B0410"
                        fontSize="14"
                        fontWeight="500"
                        opacity={active === q.idx ? 1 : 0.5}
                        style={{ transition: "opacity 400ms" }}
                      >
                        {p.range}
                      </text>
                    );
                  })}
                </g>

                {/* Center number */}
                <g style={{ pointerEvents: "none" }}>
                  <text
                    x={cx}
                    y={cy - 8}
                    textAnchor="middle"
                    className="font-display"
                    fontSize="44"
                    fontWeight="600"
                    fill="#0B0410"
                  >
                    28,000
                  </text>
                  <text
                    x={cx}
                    y={cy + 24}
                    textAnchor="middle"
                    className="font-sans"
                    fontSize="11"
                    letterSpacing="3"
                    fill="#3D1565"
                  >
                    DÍAS DE VIDA
                  </text>
                </g>
              </svg>
            </div>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-7">
            <div
              key={phase.id}
              data-testid={`lifecycle-detail-${phase.id}`}
              className="relative bg-white border border-[#0B0410]/10 rounded-sm p-8 lg:p-12 transition-all"
              style={{ boxShadow: "0 30px 80px -40px rgba(61,21,101,0.25)" }}
            >
              <div
                className="absolute top-0 left-0 w-1 h-full rounded-l-sm"
                style={{ backgroundColor: phase.color }}
              />
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="inline-block w-3 h-3 rounded-full"
                  style={{ backgroundColor: phase.color }}
                />
                <span className="text-[10px] tracking-luxury uppercase text-[#0B0410]/55">
                  {phase.label} · {phase.range} años
                </span>
              </div>
              <h3 className="font-display text-3xl lg:text-5xl font-light leading-[1.1] text-[#0B0410]">
                {phase.title}
              </h3>
              <p className="mt-5 text-base lg:text-lg text-[#3D1565] italic font-light">
                {phase.insight}
              </p>
              <p className="mt-4 text-sm lg:text-base text-[#0B0410]/75 leading-relaxed font-light max-w-2xl">
                {phase.detail}
              </p>

              <div className="mt-8 pt-6 border-t border-[#0B0410]/10">
                <div className="text-[10px] tracking-luxury uppercase text-[#3D1565] mb-4">
                  Qué pasa en esta etapa
                </div>
                <ul className="grid sm:grid-cols-2 gap-y-3 gap-x-6">
                  {phase.events.map((e) => (
                    <li
                      key={e}
                      className="flex items-start gap-3 text-sm text-[#0B0410]/85"
                    >
                      <Check
                        size={15}
                        className="shrink-0 mt-0.5"
                        style={{ color: phase.color }}
                        strokeWidth={2.4}
                      />
                      <span className="font-light">{e}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Phase pills */}
            <div className="mt-6 flex flex-wrap gap-2" data-testid="lifecycle-pills">
              {LIFE_PHASES.map((p) => (
                <button
                  key={p.id}
                  data-testid={`lifecycle-pill-${p.id}`}
                  onClick={() => setActive(p.id)}
                  className={`text-[11px] tracking-luxury uppercase px-4 py-2 rounded-sm border transition-all duration-300 ${
                    active === p.id
                      ? "bg-[#0B0410] text-[#F9F6F0] border-[#0B0410]"
                      : "bg-white text-[#0B0410]/70 border-[#0B0410]/15 hover:border-[#3D1565]"
                  }`}
                >
                  {p.range} años
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* CTA after 28,000 days hero block */}
        <div className="mt-12">
          <ScheduleCTA
            eyebrow="Tu tiempo es ahora"
            testId="lifecycle-cta-28k"
          />
        </div>

        {/* Interactive product recommendation timeline — NOW BEFORE LIFELINE */}
        <div
          data-testid="life-recommendation"
          className="mt-20 lg:mt-24 relative bg-gradient-to-br from-[#150A1F] via-[#0B0410] to-[#150A1F] backdrop-blur-sm border border-[#E6D5B8]/20 rounded-sm p-8 lg:p-12 overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-[#E6D5B8]/5 blur-[110px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-[#3D1565]/30 blur-[110px] pointer-events-none" />

          <div className="relative">
            <div className="text-center mb-10">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Sparkles size={16} className="text-[#E6D5B8]" />
                <span className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] font-medium">
                  Recomendación según tu edad
                </span>
              </div>
              <h3 className="font-display text-2xl lg:text-4xl font-light leading-tight text-[#F9F6F0]">
                Desliza la línea — descubre los productos que{" "}
                <span className="italic text-[#E6D5B8]">deberías tener</span> en
                cada etapa.
              </h3>
            </div>

            {/* Slider */}
            <div className="max-w-4xl mx-auto" data-testid="rec-slider-wrap">
              <div className="relative">
                <Slider
                  data-testid="rec-age-slider"
                  value={[recAge]}
                  onValueChange={(v) => setRecAge(v[0])}
                  min={0}
                  max={80}
                  step={1}
                  className="[&_[role=slider]]:bg-[#E6D5B8] [&_[role=slider]]:border-[#E6D5B8] [&_[role=slider]]:w-6 [&_[role=slider]]:h-6 [&_[data-orientation=horizontal]]:bg-white/10 [&_[data-orientation=horizontal]>span]:bg-gradient-to-r [&_[data-orientation=horizontal]>span]:from-[#E6D5B8] [&_[data-orientation=horizontal]>span]:to-[#3D1565]"
                />
                <div className="absolute inset-x-0 -bottom-1 flex justify-between text-[10px] tracking-luxury uppercase text-[#A397B4] pointer-events-none px-1">
                  {[0, 15, 25, 40, 55, 65, 80].map((m) => (
                    <span key={m}>{m}</span>
                  ))}
                </div>
              </div>

              <div className="mt-10 text-center">
                <div className="text-[10px] tracking-luxury uppercase text-[#A397B4]">
                  Tienes
                </div>
                <div
                  data-testid="rec-age-value"
                  className="font-display text-5xl lg:text-6xl font-light text-[#E6D5B8] leading-none my-2"
                >
                  {recAge}
                  <span className="font-sans-emb text-base text-[#A397B4] ml-2 align-middle tracking-luxury">
                    AÑOS
                  </span>
                </div>
              </div>
            </div>

            {/* Recommendation card */}
            <div
              key={recommendation.stage}
              data-testid={`rec-card-${recommendation.stage}`}
              className="mt-10 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start bg-gradient-to-br from-[#150A1F] to-[#0B0410] border border-[#3D1565] rounded-sm p-8 lg:p-10"
            >
              <div className="lg:col-span-5">
                <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mb-3">
                  {recommendation.phase} · {recommendation.minAge}—{recommendation.maxAge} años
                </div>
                <h4 className="font-display text-3xl lg:text-4xl font-light leading-[1.1] text-[#F9F6F0]">
                  {recommendation.stage}
                </h4>
                <p className="mt-3 font-display text-lg lg:text-xl italic text-[#E6D5B8] font-light leading-snug">
                  {recommendation.title}
                </p>
                <p className="mt-5 text-sm lg:text-base text-[#F9F6F0]/70 leading-relaxed font-light">
                  {recommendation.desc}
                </p>
              </div>

              <div className="lg:col-span-7">
                <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-4 flex items-center gap-2">
                  <span className="h-px w-6 bg-[#E6D5B8]" />
                  Productos recomendados
                </div>
                <ul className="grid sm:grid-cols-2 gap-y-3 gap-x-6">
                  {recommendation.products.map((p) => (
                    <li
                      key={p}
                      data-testid={`rec-product-${p.slice(0, 12)}`}
                      className="flex items-start gap-3 text-sm text-[#F9F6F0]/90"
                    >
                      <Check
                        size={15}
                        className="text-[#E6D5B8] shrink-0 mt-0.5"
                        strokeWidth={2.2}
                      />
                      <span className="font-light">{p}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#contacto"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById("contacto")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  data-testid="rec-cta"
                  className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-luxury uppercase text-[#E6D5B8] hover:text-[#F9F6F0] border-b border-[#E6D5B8]/40 hover:border-[#F9F6F0] pb-1 transition-colors"
                >
                  Diseñar mi plan
                  <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* CTA after Age Recommendation */}
        <div className="mt-12">
          <ScheduleCTA
            eyebrow="Tu plan a la medida"
            testId="lifecycle-cta-recommendation"
          />
        </div>

        {/* BIRTH ↔ DEATH timeline */}
        <div
          data-testid="lifeline-timeline"
          className="mt-20 lg:mt-24 relative bg-gradient-to-br from-[#150A1F] via-[#0B0410] to-[#150A1F] text-[#F9F6F0] rounded-sm border border-[#3D1565] overflow-hidden"
        >
          {/* Decorative glow */}
          <div className="absolute top-0 left-1/4 w-72 h-72 rounded-full bg-[#E6D5B8]/5 blur-[110px] pointer-events-none" />
          <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full bg-[#3D1565]/30 blur-[110px] pointer-events-none" />

          <div className="relative p-8 lg:p-16">
            <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
              <div className="flex items-center gap-3 mb-5 justify-center">
                <span className="h-px w-10 bg-[#E6D5B8]" />
                <span className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] font-medium">
                  La línea de tu vida
                </span>
                <span className="h-px w-10 bg-[#E6D5B8]" />
              </div>
              <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.1]">
                Dos fechas que{" "}
                <span className="italic text-[#E6D5B8]">no decidimos</span>.
                <br className="hidden sm:block" /> Lo que está en medio,{" "}
                <span className="italic text-[#E6D5B8]">sí</span>.
              </h3>
            </div>

            {/* The line itself */}
            <div className="relative max-w-5xl mx-auto" data-testid="lifeline-line">
              {/* Horizontal track */}
              <div className="relative grid grid-cols-2 gap-6 sm:gap-12 items-start">
                {/* BIRTH endpoint */}
                <div className="flex flex-col items-start" data-testid="lifeline-birth">
                  <div className="flex items-center gap-4 w-full">
                    <div className="relative w-14 h-14 flex items-center justify-center bg-[#F9F6F0] rounded-full border border-[#E6D5B8] shrink-0">
                      <span className="font-display text-2xl text-[#0B0410]">α</span>
                      <span className="absolute inset-0 rounded-full border border-[#E6D5B8]/40 animate-ping" style={{ animationDuration: "3s" }} />
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-r from-[#E6D5B8] to-[#E6D5B8]/30" />
                  </div>
                  <div className="mt-5">
                    <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-2">
                      Nacimiento
                    </div>
                    <div className="font-display text-2xl lg:text-3xl text-[#F9F6F0] leading-tight">
                      El día en que llegamos
                    </div>
                    <p className="mt-2 text-sm text-[#F9F6F0]/65 font-light leading-relaxed max-w-xs">
                      No lo elegimos. No lo planeamos. Es el inicio incondicional.
                    </p>
                  </div>
                </div>

                {/* DEATH endpoint */}
                <div className="flex flex-col items-end" data-testid="lifeline-death">
                  <div className="flex items-center gap-4 w-full">
                    <div className="flex-1 h-px bg-gradient-to-l from-[#3D1565] to-[#3D1565]/30" />
                    <div className="relative w-14 h-14 flex items-center justify-center bg-[#3D1565] rounded-full border border-[#E6D5B8] shrink-0">
                      <span className="font-display text-2xl text-[#E6D5B8]">Ω</span>
                      <span className="absolute inset-0 rounded-full border border-[#3D1565]/60 animate-ping" style={{ animationDuration: "4s" }} />
                    </div>
                  </div>
                  <div className="mt-5 text-right">
                    <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-2">
                      Fin del ciclo
                    </div>
                    <div className="font-display text-2xl lg:text-3xl text-[#F9F6F0] leading-tight">
                      El día en que partimos
                    </div>
                    <p className="mt-2 text-sm text-[#F9F6F0]/65 font-light leading-relaxed max-w-xs ml-auto">
                      Tampoco lo elegimos. Llega cuando llega. Sin aviso ni
                      acuerdo.
                    </p>
                  </div>
                </div>
              </div>

              {/* Middle pillars row — what we DO control */}
              <div className="relative mt-14 lg:mt-20">
                {/* Connecting line behind the pillars */}
                <div className="absolute top-7 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E6D5B8]/40 to-transparent" />

                <div className="text-center mb-10 lg:mb-12">
                  <div className="inline-block px-5 py-2 bg-[#3D1565]/30 border border-[#E6D5B8]/30 rounded-sm">
                    <span className="text-[11px] tracking-luxury uppercase text-[#E6D5B8]">
                      lo que sí está en tus manos
                    </span>
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-6 lg:gap-10 relative">
                  {[
                    {
                      key: "proteger",
                      icon: "shield",
                      title: "Proteger",
                      text: "Tu salud, tu familia y tu patrimonio. Las decisiones que toman fuerza cuando algo no sale como pensaste.",
                    },
                    {
                      key: "disfrutar",
                      icon: "heart",
                      title: "Disfrutar",
                      text: "Cada etapa tiene su propio sabor. Viajar, formar familia, emprender, retirarte. Vivirlas con tranquilidad financiera.",
                    },
                    {
                      key: "prever",
                      icon: "compass",
                      title: "Prever",
                      text: "Pensar en el mañana cuando hoy estás bien. Ahorrar e invertir antes de necesitarlo — ahí está la libertad.",
                    },
                  ].map((p) => (
                    <div
                      key={p.key}
                      data-testid={`lifeline-pillar-${p.key}`}
                      className="relative bg-[#0B0410]/60 backdrop-blur-sm border border-[#3D1565] hover:border-[#E6D5B8]/50 rounded-sm p-6 lg:p-8 transition-all duration-500 hover:-translate-y-1 group"
                    >
                      <div className="absolute -top-7 left-1/2 -translate-x-1/2 w-14 h-14 flex items-center justify-center bg-[#3D1565] border border-[#E6D5B8]/40 rounded-full group-hover:border-[#E6D5B8] transition-colors">
                        {p.icon === "shield" ? (
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E6D5B8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                          </svg>
                        ) : p.icon === "heart" ? (
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E6D5B8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                          </svg>
                        ) : (
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E6D5B8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
                          </svg>
                        )}
                      </div>
                      <div className="mt-6 text-center">
                        <h4 className="font-display text-2xl lg:text-3xl font-light text-[#F9F6F0] mb-3">
                          {p.title}
                        </h4>
                        <p className="text-sm text-[#F9F6F0]/70 leading-relaxed font-light">
                          {p.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Closing line */}
              <div className="mt-12 lg:mt-16 text-center max-w-2xl mx-auto">
                <p className="font-display text-xl lg:text-2xl italic text-[#E6D5B8] leading-snug">
                  &ldquo;Vivimos una sola vez. Pero si lo haces bien,{" "}
                  <span className="not-italic text-[#F9F6F0]">una vez es suficiente</span>.&rdquo;
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Capital Humano × Capital Financiero — crossing curves visualization */}
        <div
          data-testid="capitals-crossing"
          className="mt-20 lg:mt-24 relative bg-[#0B0410] text-[#F9F6F0] rounded-sm overflow-hidden border border-[#3D1565]"
        >
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#3D1565]/30 to-transparent pointer-events-none" />

          <div className="relative grid lg:grid-cols-12 gap-0">
            {/* LEFT — narrative */}
            <div className="lg:col-span-5 p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-white/10">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-[#E6D5B8]" />
                <span className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] font-medium">
                  El cruce de la vida
                </span>
              </div>
              <h3 className="font-display text-3xl lg:text-4xl font-light leading-[1.15]">
                Tu capital humano{" "}
                <span className="italic text-[#E6D5B8]">desciende</span>.
                Tu capital financiero{" "}
                <span className="italic text-[#E6D5B8]">debería subir</span>.
              </h3>
              <p className="mt-6 text-sm lg:text-base text-[#F9F6F0]/70 leading-relaxed font-light">
                Las dos curvas se cruzan alrededor de los <b className="text-[#E6D5B8] font-medium">60 años</b>.
                Ese cruce define todo. Si tu capital financiero llega a tiempo,
                tu vejez es libre. Si no, dependes de otros — o sigues
                trabajando.
              </p>

              <div className="mt-8 grid grid-cols-2 gap-6 pt-6 border-t border-white/10">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-px bg-[#C13B3B]" style={{ borderTop: "2px dashed #C13B3B" }} />
                    <span className="text-[10px] tracking-luxury uppercase text-[#A397B4]">
                      Capital Humano
                    </span>
                  </div>
                  <p className="text-xs text-[#F9F6F0]/60 leading-relaxed font-light">
                    Tu capacidad de ganar y ahorrar dinero. Es finito.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-0.5 bg-[#E6D5B8]" />
                    <span className="text-[10px] tracking-luxury uppercase text-[#A397B4]">
                      Capital Financiero
                    </span>
                  </div>
                  <p className="text-xs text-[#F9F6F0]/60 leading-relaxed font-light">
                    Los activos que construyes. Lo único que te sostiene al
                    final.
                  </p>
                </div>
              </div>
            </div>

            {/* RIGHT — chart */}
            <div className="lg:col-span-7 p-6 lg:p-10">
              <svg
                viewBox="0 0 600 360"
                className="w-full h-auto"
                data-testid="capitals-chart"
                preserveAspectRatio="xMidYMid meet"
              >
                <defs>
                  <linearGradient id="humanGrad" x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0%" stopColor="#C13B3B" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#C13B3B" stopOpacity="0.3" />
                  </linearGradient>
                  <linearGradient id="finGrad" x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0%" stopColor="#E6D5B8" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#E6D5B8" stopOpacity="1" />
                  </linearGradient>
                  <linearGradient id="humanFill" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#C13B3B" stopOpacity="0.15" />
                    <stop offset="100%" stopColor="#C13B3B" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="finFill" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#E6D5B8" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#E6D5B8" stopOpacity="0" />
                  </linearGradient>
                </defs>

                {/* Subtle horizontal grid */}
                {[0, 1, 2, 3, 4].map((i) => (
                  <line
                    key={i}
                    x1="40"
                    x2="580"
                    y1={50 + i * 60}
                    y2={50 + i * 60}
                    stroke="#F9F6F0"
                    strokeOpacity="0.05"
                  />
                ))}

                {/* X axis */}
                <line x1="40" x2="580" y1="310" y2="310" stroke="#F9F6F0" strokeOpacity="0.15" />

                {/* X-axis labels (ages) */}
                {[
                  { x: 40, label: "0" },
                  { x: 175, label: "20" },
                  { x: 310, label: "40" },
                  { x: 445, label: "60" },
                  { x: 580, label: "80+" },
                ].map((t) => (
                  <text
                    key={t.label}
                    x={t.x}
                    y={332}
                    fill="#A397B4"
                    fontSize="11"
                    textAnchor="middle"
                    letterSpacing="2"
                  >
                    {t.label}
                  </text>
                ))}
                <text
                  x="310"
                  y="352"
                  fill="#A397B4"
                  fontSize="10"
                  textAnchor="middle"
                  letterSpacing="3"
                  style={{ textTransform: "uppercase" }}
                >
                  Años de vida
                </text>

                {/* Capital Humano area (dashed line + faint fill) — DYNAMIC */}
                <path d={humanFill} fill="url(#humanFill)" />
                <path
                  d={humanPath}
                  fill="none"
                  stroke="url(#humanGrad)"
                  strokeWidth="2.5"
                  strokeDasharray="6 5"
                  strokeLinecap="round"
                  style={{ transition: "d 400ms ease" }}
                />

                {/* Capital Financiero area — DYNAMIC */}
                <path d={finFill} fill="url(#finFill)" />
                <path
                  d={finPath}
                  fill="none"
                  stroke="url(#finGrad)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  style={{ transition: "d 400ms ease" }}
                />

                {/* Cross point marker — DYNAMIC */}
                <g style={{ transition: "transform 400ms ease" }}>
                  <line
                    x1={crossX}
                    x2={crossX}
                    y1={Y_CROSS}
                    y2={310}
                    stroke="#E6D5B8"
                    strokeOpacity="0.35"
                    strokeDasharray="3 4"
                  />
                  <circle cx={crossX} cy={Y_CROSS} r="14" fill="#0B0410" stroke="#E6D5B8" strokeWidth="1.5" />
                  <circle cx={crossX} cy={Y_CROSS} r="5" fill="#E6D5B8" />
                </g>
                <g transform={`translate(${crossX}, ${Y_CROSS})`}>
                  <rect
                    x="-72"
                    y="-58"
                    width="144"
                    height="42"
                    rx="3"
                    fill="#150A1F"
                    stroke="#E6D5B8"
                    strokeOpacity="0.35"
                  />
                  <text x="0" y="-40" fill="#E6D5B8" fontSize="10" textAnchor="middle" letterSpacing="3" style={{ textTransform: "uppercase" }}>
                    Punto de cruce
                  </text>
                  <text x="0" y="-20" fill="#F9F6F0" fontSize="15" textAnchor="middle" fontFamily="Cormorant Garamond, serif" fontWeight="500">
                    ≈ <tspan fontStyle="italic" fill="#E6D5B8">{crossAge}</tspan> años
                  </text>
                </g>

                {/* Inline curve labels */}
                <text
                  x="80"
                  y="58"
                  fill="#C13B3B"
                  fontSize="11"
                  letterSpacing="3"
                  style={{ textTransform: "uppercase" }}
                >
                  Capital humano
                </text>
                <text
                  x="500"
                  y="44"
                  fill="#E6D5B8"
                  fontSize="11"
                  letterSpacing="3"
                  textAnchor="end"
                  style={{ textTransform: "uppercase" }}
                >
                  Capital financiero
                </text>
              </svg>

              {/* Interactive slider — move the cross point */}
              <div
                data-testid="cross-age-slider-wrap"
                className="mt-6 lg:mt-8 px-2"
              >
                <div className="flex items-baseline justify-between mb-3">
                  <span className="text-[10px] tracking-luxury uppercase text-[#A397B4]">
                    Mueve el punto de cruce
                  </span>
                  <span className="text-[10px] tracking-luxury uppercase text-[#E6D5B8]">
                    <span
                      data-testid="cross-age-value"
                      className="font-display text-2xl text-[#F9F6F0] mr-1 normal-case tracking-normal italic"
                    >
                      {crossAge}
                    </span>
                    años
                  </span>
                </div>
                <Slider
                  data-testid="cross-age-slider"
                  value={[crossAge]}
                  onValueChange={(v) => setCrossAge(v[0])}
                  min={0}
                  max={65}
                  step={1}
                  className="[&_[role=slider]]:bg-[#E6D5B8] [&_[role=slider]]:border-[#E6D5B8] [&_[role=slider]]:w-5 [&_[role=slider]]:h-5 [&_[data-orientation=horizontal]]:bg-white/10 [&_[data-orientation=horizontal]>span]:bg-[#E6D5B8]"
                />
                <div className="flex justify-between mt-2 text-[10px] tracking-luxury uppercase text-[#A397B4]/70">
                  <span>0</span>
                  <span>20</span>
                  <span>40</span>
                  <span>65</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA after lifeline block */}
        <div className="mt-12">
          <ScheduleCTA
            eyebrow="No esperes a mañana"
            testId="lifecycle-cta-final"
          />
        </div>

      </div>
    </section>
  );
};

export default LifeCycle;
